import { useEffect } from 'react';
import { getFirestore, collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { getMessaging, getToken } from 'firebase/messaging';
import { toast } from 'react-toastify';

// Custom hook to check for upcoming meetings
export const useReminderChecker = () => {
  useEffect(() => {
    // Function to check for upcoming meetings
    const checkUpcomingMeetings = async () => {
      try {
        // Get current time in UTC
        const now = new Date();
        const nowUTC = new Date(now.toISOString());
        
        // Get time 5 minutes from now in UTC
        const fiveMinutesLater = new Date(nowUTC);
        fiveMinutesLater.setMinutes(fiveMinutesLater.getMinutes() + 5);
        
        // Convert to ISO strings for comparison
        const minTime = nowUTC.toISOString();
        const maxTime = fiveMinutesLater.toISOString();
        
        // Query for reminders that are due within the next 5 minutes
        const db = getFirestore();
        const remindersRef = collection(db, "reminders");
        const reminderQuery = query(
          remindersRef,
          where("notificationSent", "==", false),
          where("reminderDate", ">=", minTime),
          where("reminderDate", "<=", maxTime)
        );
        
        const remindersSnapshot = await getDocs(reminderQuery);
        
        if (remindersSnapshot.empty) {
          console.log("No upcoming meetings to notify");
          return;
        }
        
        // Check if we have permission to send notifications
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.log('Notification permission denied');
          return;
        }
        
        // Process each reminder
        remindersSnapshot.forEach(async (document) => {
          const reminderData = document.data();
          const { clientName, meetingDate } = reminderData;
          
          // Extract meeting time for display
          const meetingDateTime = new Date(meetingDate);
          const formattedTime = meetingDateTime.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit"
          });
          
          // Show browser notification
          const notification = new Notification('Upcoming Meeting Reminder', {
            body: `You have a meeting with ${clientName} at ${formattedTime}`,
            icon: '/logo192.png'
          });
          
          // Show toast notification as well
          toast.info(`Reminder: Meeting with ${clientName} at ${formattedTime}`);
          
          // Dispatch a custom event to notify the chat
          window.dispatchEvent(new CustomEvent('reminder-chat-message', {
            detail: {
              clientName,
              meetingDate,
              message: `You have a meeting with ${clientName} in half an hour, please be ready.`,
              createdAt: new Date().toISOString()
            }
          }));
          
          // Update reminder as sent
          const reminderRef = doc(db, 'reminders', document.id);
          await updateDoc(reminderRef, { notificationSent: true });
          
          // Handle notification click
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        });
        
      } catch (error) {
        console.error("Error checking meetings:", error);
      }
    };
    
    // Run the check when component mounts
    checkUpcomingMeetings();
    
    // Set up interval to check every 2 minutes
    const intervalId = setInterval(checkUpcomingMeetings, 1 * 60 * 1000);
    
    // Clean up interval when component unmounts
    return () => clearInterval(intervalId);
  }, []);
};

// Custom hook to handle app visibility changes
export const useVisibilityHandler = () => {
  useEffect(() => {
    // Function to check reminders when page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // The page is now visible, check for reminders
        const checkUpcomingMeetings = new CustomEvent('check-reminders');
        window.dispatchEvent(checkUpcomingMeetings);
      }
    };
    
    // Add event listener for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Clean up event listener
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};

// Service worker registration for background notifications
export const registerServiceWorker = async () => {
  // Check if browser supports service workers
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      
      // Request notification permission if not already granted
      if (Notification.permission !== 'granted') {
        await Notification.requestPermission();
      }
    } catch (error) {
      console.error('Service worker registration failed:', error);
    }
  } else {
    console.log('Service workers not supported in this browser');
  }
};